import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Divider,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import whatsappService, { WhatsappGroup } from '../services/whatsappService';

interface GroupSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectGroup: (groupId: string, groupName: string) => void;
  apiKey?: string;
}

const GroupSelectionDialog: React.FC<GroupSelectionDialogProps> = ({
  open,
  onClose,
  onSelectGroup,
  apiKey,
}) => {
  const [groups, setGroups] = useState<WhatsappGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const fetchGroups = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const storedKey = (apiKey ?? '').trim();
      if (!storedKey) {
        setError('API Key belum diisi. Isi dan simpan konfigurasi StarSender terlebih dahulu.');
        return;
      }

      // Jika API key disamarkan (****), backend memakai key asli dari database
      const keyForRequest = storedKey.startsWith('****') ? undefined : storedKey;
      const response = await whatsappService.getGroups(keyForRequest);

      if (!response.success) {
        throw new Error(response.error || response.message || 'Gagal mengambil daftar grup');
      }

      const groupsData: WhatsappGroup[] = Array.isArray(response.data) ? response.data : [];
      setGroups(groupsData);
    } catch (error: any) {
      console.error('Error fetching groups:', error);
      setError(
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.message || 
        'Gagal mengambil daftar grup'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchGroups();
    }
  }, [open, apiKey]);

  const handleSelectGroup = () => {
    if (selectedGroupId) {
      const selectedGroup = groups.find((group) => group.id === selectedGroupId);
      const groupName = selectedGroup?.name || selectedGroup?.subject || selectedGroupId;
      const sendTarget =
        selectedGroup?.sendTarget || selectedGroup?.name || selectedGroup?.subject || groupName;
      // StarSender /send/grup membutuhkan Nama Grup di field `to`
      onSelectGroup(sendTarget, groupName);
      onClose();
    }
  };

  const handleGroupClick = (groupId: string) => {
    setSelectedGroupId(groupId);
  };

  const formatGroupName = (group: WhatsappGroup) => {
    return group.name || group.subject || group.id || 'Unknown Group';
  };

  const formatGroupInfo = (group: WhatsappGroup) => {
    const parts = [];
    if (group.participants) {
      parts.push(`${group.participants} anggota`);
    }
    if (group.id) {
      parts.push(`ID: ${group.id.substring(0, 20)}...`);
    }
    return parts.join(' • ');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Pilih Grup WhatsApp</Typography>
          <Box>
            <IconButton onClick={fetchGroups} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Mengambil daftar grup...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button 
              size="small" 
              onClick={fetchGroups}
              sx={{ ml: 1 }}
            >
              Coba Lagi
            </Button>
          </Alert>
        )}

        {!loading && !error && groups.length === 0 && (
          <Alert severity="info">
            Tidak ada grup ditemukan. Pastikan konfigurasi WhatsApp sudah benar.
          </Alert>
        )}

        {!loading && groups.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pilih grup untuk pengiriman notifikasi billing:
            </Typography>
            <List>
              {groups.map((group, index) => (
                <React.Fragment key={group.id || index}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedGroupId === group.id}
                      onClick={() => handleGroupClick(group.id)}
                    >
                      <ListItemText
                        primary={formatGroupName(group)}
                        secondary={formatGroupInfo(group)}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < groups.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Batal
        </Button>
        <Button 
          onClick={handleSelectGroup}
          variant="contained"
          disabled={!selectedGroupId}
        >
          Pilih Grup
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupSelectionDialog;
